import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Eye, Edit2, MoreVertical, Send, XCircle,
    Users, Award, Clock, CheckCircle, FileText
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem, DropdownDivider,
    ConfirmDialog, toast
} from '../../components/common';
import { rfqApi } from '../../services/api';
import './RFQ.css';

export default function RFQList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionRFQ, setActionRFQ] = useState(null);
    const [actionType, setActionType] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['rfqs', page, pageSize, search, statusFilter],
        queryFn: () => rfqApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ id, action }) => {
            switch (action) {
                case 'publish': return rfqApi.publish(id);
                case 'close': return rfqApi.close(id);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`RFQ ${action}ed successfully`);
            queryClient.invalidateQueries(['rfqs']);
            setActionRFQ(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': { variant: 'default', icon: FileText },
            'published': { variant: 'info', icon: Send },
            'in_progress': { variant: 'warning', icon: Clock },
            'closed': { variant: 'default', icon: XCircle },
            'awarded': { variant: 'success', icon: Award },
            'cancelled': { variant: 'danger', icon: XCircle }
        };
        const { variant } = config[status] || { variant: 'default' };
        return <Badge variant={variant}>{status?.replace('_', ' ')}</Badge>;
    };

    const columns = [
        {
            key: 'rfqNumber',
            title: 'RFQ Number',
            dataIndex: 'rfqNumber',
            render: (num) => <span className="rfq-number">{num}</span>
        },
        {
            key: 'title',
            title: 'Title',
            dataIndex: 'title',
            render: (title, row) => (
                <div className="rfq-title-cell">
                    <span className="rfq-title">{title}</span>
                    <span className="rfq-pr">From: {row.purchaseRequest?.prNumber || '-'}</span>
                </div>
            )
        },
        {
            key: 'deadline',
            title: 'Deadline',
            dataIndex: 'submissionDeadline',
            render: (date) => formatDate(date)
        },
        {
            key: 'vendors',
            title: 'Vendors',
            render: (_, row) => (
                <div className="vendor-count">
                    <Users size={14} />
                    {row.invitedVendors?.length || 0} invited
                </div>
            )
        },
        {
            key: 'quotations',
            title: 'Quotations',
            render: (_, row) => (
                <span className="quotation-count">
                    {row.quotations?.length || 0} received
                </span>
            )
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); navigate(`/rfq/${row.id}`); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'draft' && (
                                <>
                                    <DropdownItem icon={Edit2} onClick={() => { close(); navigate(`/rfq/${row.id}/edit`); }}>
                                        Edit
                                    </DropdownItem>
                                    <DropdownItem icon={Users} onClick={() => { close(); navigate(`/rfq/${row.id}/invite`); }}>
                                        Invite Vendors
                                    </DropdownItem>
                                    <DropdownDivider />
                                    <DropdownItem icon={Send} onClick={() => { close(); setActionRFQ(row); setActionType('publish'); }}>
                                        Publish
                                    </DropdownItem>
                                </>
                            )}
                            {row.status === 'published' && (
                                <DropdownItem icon={XCircle} onClick={() => { close(); setActionRFQ(row); setActionType('close'); }}>
                                    Close RFQ
                                </DropdownItem>
                            )}
                            {row.status === 'closed' && (
                                <DropdownItem icon={Award} onClick={() => { close(); navigate(`/rfq/${row.id}/award`); }}>
                                    Award Vendor
                                </DropdownItem>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'closed', label: 'Closed' },
        { value: 'awarded', label: 'Awarded' }
    ];

    return (
        <div className="rfq-page">
            <PageHeader
                title="RFQ Management"
                subtitle="Manage Request for Quotations"
                breadcrumbs={[{ label: 'RFQ', path: '/rfq' }]}
                actions={
                    <Button icon={Plus} onClick={() => navigate('/rfq/create')}>
                        Create RFQ
                    </Button>
                }
            />

            <Card>
                <div className="rfq-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search RFQs..."
                        className="rfq-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="rfq-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No RFQs found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="rfq-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={!!actionRFQ}
                onClose={() => { setActionRFQ(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionRFQ?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} RFQ`}
                message={`Are you sure you want to ${actionType} this RFQ?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
